import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Input } from "antd";

const { TextArea } = Input;

/** Get pixel position of caret at offset within a textarea (mirror technique) */
function getCaretCoordinates(ta, offset) {
  const style = getComputedStyle(ta);
  const mirror = document.createElement("div");
  const mirrorStyle = mirror.style;
  mirrorStyle.position = "absolute";
  mirrorStyle.visibility = "hidden";
  mirrorStyle.whiteSpace = "pre-wrap";
  mirrorStyle.wordWrap = "break-word";
  mirrorStyle.top = "0";
  mirrorStyle.left = "-9999px";
  mirrorStyle.width = ta.offsetWidth + "px";
  mirrorStyle.font = style.font;
  mirrorStyle.fontSize = style.fontSize;
  mirrorStyle.fontFamily = style.fontFamily;
  mirrorStyle.lineHeight = style.lineHeight;
  mirrorStyle.padding = style.padding;
  mirrorStyle.border = style.border;
  mirrorStyle.boxSizing = style.boxSizing;
  mirrorStyle.letterSpacing = style.letterSpacing;
  const text = (ta.value || "").substring(0, offset);
  const span = document.createElement("span");
  mirror.appendChild(document.createTextNode(text));
  mirror.appendChild(span);
  document.body.appendChild(mirror);
  const caretX = span.offsetLeft - ta.scrollLeft;
  const caretY = span.offsetTop - ta.scrollTop;
  document.body.removeChild(mirror);
  return { left: caretX, top: caretY };
}

/** TextArea that shows placeholder suggestions when user types [ or [{ */
export default function PlaceholderSuggestTextArea({
  value,
  onChange,
  suggestionKeys = [],
  getLabel = (k) => k,
  onBlur,
  ...rest
}) {
  const wrapperRef = useRef(null);
  const textareaRef = useRef(null);
  const cursorAfterInsertRef = useRef(null);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [filter, setFilter] = useState("");
  const [anchorStart, setAnchorStart] = useState(0);
  const [cursorOffset, setCursorOffset] = useState(0);
  const [dropdownPosition, setDropdownPosition] = useState({ left: 0, top: 0 });
  const blurTimerRef = useRef(null);

  const filterStripped = (filter || "").replace(/^\{+/, "");
  const filtered = (suggestionKeys || [])
    .filter((k) => !filterStripped || String(k).toLowerCase().startsWith(filterStripped.toLowerCase()))
    .slice(0, 12);

  const handleChange = (e) => {
    const ta = e.target;
    const val = ta.value;
    const start = ta.selectionStart ?? 0;
    const textBefore = val.slice(0, start);
    const lastBracket = textBefore.lastIndexOf("[");
    if (lastBracket >= 0 && textBefore.slice(lastBracket).indexOf("]") === -1) {
      setShowSuggestions(true);
      setFilter(textBefore.slice(lastBracket + 1));
      setAnchorStart(lastBracket);
      setCursorOffset(start);
    } else {
      setShowSuggestions(false);
    }
    onChange?.(e);
  };

  const getNativeTextarea = () => {
    const ref = textareaRef.current;
    if (!ref) return null;
    return ref.resizableTextArea?.textArea ?? ref.input ?? ref;
  };

  const insertSuggestion = (key) => {
    const ta = getNativeTextarea();
    const val = value ?? "";
    const start = ta?.selectionStart ?? anchorStart + 1;
    const insertText = key + "]";
    const newVal = val.slice(0, anchorStart) + "[" + insertText + val.slice(start);
    const newCursor = anchorStart + insertText.length + 1;
    cursorAfterInsertRef.current = newCursor;
    onChange?.({ target: { value: newVal } });
    setShowSuggestions(false);
  };

  useEffect(() => {
    const pos = cursorAfterInsertRef.current;
    if (pos == null) return;
    cursorAfterInsertRef.current = null;
    const ta = getNativeTextarea();
    if (ta && value != null && typeof ta.setSelectionRange === "function") {
      ta.focus();
      ta.setSelectionRange(pos, pos);
    }
  }, [value]);

  const handleKeyDown = (e) => {
    if (showSuggestions && filtered.length > 0) {
      if (e.key === "Escape") {
        setShowSuggestions(false);
        e.preventDefault();
      } else if (e.key === "Enter" || e.key === "Tab") {
        insertSuggestion(filtered[0]);
        e.preventDefault();
      }
    }
  };

  const handleBlur = () => {
    blurTimerRef.current = setTimeout(() => setShowSuggestions(false), 200);
    onBlur?.();
  };

  const handleFocus = () => {
    if (blurTimerRef.current) clearTimeout(blurTimerRef.current);
  };

  useLayoutEffect(() => {
    if (!showSuggestions || filtered.length === 0) return;
    const ta = getNativeTextarea();
    const wrapper = wrapperRef.current;
    if (!ta || !wrapper) return;
    try {
      const offset = ta.selectionStart ?? cursorOffset;
      const { left: caretX, top: caretY } = getCaretCoordinates(ta, offset);
      const style = getComputedStyle(ta);
      const paddingLeft = parseFloat(style.paddingLeft) || 0;
      const paddingTop = parseFloat(style.paddingTop) || 0;
      const lineHeight = parseFloat(style.lineHeight) || 20;
      const taRect = ta.getBoundingClientRect();
      const wrapperRect = wrapper.getBoundingClientRect();
      let left = taRect.left - wrapperRect.left + paddingLeft + caretX;
      const top = taRect.top - wrapperRect.top + paddingTop + caretY + lineHeight;
      const minWidth = 260;
      left = Math.max(0, Math.min(left, wrapper.offsetWidth - minWidth));
      setDropdownPosition({ left, top });
    } catch (err) {
      setDropdownPosition({ left: 0, top: "100%" });
    }
  }, [showSuggestions, filtered.length, cursorOffset, value]);

  return (
    <div ref={wrapperRef} style={{ position: "relative" }}>
      <TextArea
        ref={textareaRef}
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        onFocus={handleFocus}
        {...rest}
      />
      {showSuggestions && filtered.length > 0 && (
        <div
          className="letter-placeholder-suggestions"
          role="listbox"
          style={{
            position: "absolute",
            left: dropdownPosition.left,
            top: typeof dropdownPosition.top === "number" ? dropdownPosition.top + 2 : dropdownPosition.top,
            background: "#fff",
            border: "1px solid #d9d9d9",
            borderRadius: 6,
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
            maxHeight: 220,
            overflowY: "auto",
            zIndex: 1050,
            minWidth: 260,
          }}
        >
          {filtered.map((key) => (
            <div
              key={key}
              role="option"
              onClick={() => insertSuggestion(key)}
              style={{
                padding: "8px 12px",
                cursor: "pointer",
                fontSize: 12,
                borderBottom: "1px solid #f0f0f0",
              }}
              onMouseDown={(e) => e.preventDefault()}
            >
              <code style={{ marginRight: 8 }}>[{key}]</code>
              <span style={{ color: "#8c8c8c" }}>{getLabel(key)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
